// AllProducts — /admin/products. Cross-user product rollup,
// newest-first by updated_at. Operator landing surface for "what
// products exist across the system."
//
// Columns: product_id (click-through), owner (user_id), idea
// (truncated), product_type, created/updated, run_count, latest_run
// (click-through to its RunTrace).
//
// Backend: GET /api/v1/admin/products (admin_cross_user.go).

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { listAllProducts } from "../api/traceApi";
import { Table, THead, TBody, TR, TH, TD } from "../components/Table";

function formatTs(ts?: string): string {
  if (!ts) return "—";
  return ts.slice(0, 19).replace("T", " ");
}

export function AllProducts() {
  const { data: me } = useUser();
  const q = useQuery({
    queryKey: ["admin", "all-products"],
    queryFn: () => listAllProducts(100),
  });

  if (!me) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-10">
        <h1 className="text-display font-medium text-ink-50 tracking-tightest mb-2">
          All products
        </h1>
        <p className="font-mono text-[11px] text-ink-400 mb-6">
          Products across every user · newest first by updated_at · sample of 100
        </p>

        {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}
        {q.error && (
          <p className="text-sm text-rose-300">
            Failed: {(q.error as Error).message}
          </p>
        )}

        {q.data && (
          <Table>
            <THead>
              <TH>product</TH>
              <TH>owner</TH>
              <TH>type</TH>
              <TH>created</TH>
              <TH>updated</TH>
              <TH>runs</TH>
              <TH>latest run</TH>
            </THead>
            <TBody>
              {q.data.map((p) => (
                <TR key={`${p.user_id}/${p.id}`}>
                  <TD>
                    {p.user_id && p.id ? (
                      <Link
                        to={`/users/${p.user_id}/products/${p.id}`}
                        className="font-mono text-[11px] text-ink-100 hover:text-accent"
                      >
                        {p.id}
                      </Link>
                    ) : (
                      <span className="font-mono text-[11px] text-ink-300">
                        {p.id ?? "—"}
                      </span>
                    )}
                    <div
                      className="text-[10px] text-ink-500 mt-0.5 truncate max-w-md"
                      title={p.idea ?? ""}
                    >
                      {p.idea ?? "—"}
                    </div>
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {p.user_id ? (
                      <Link to={`/users/${p.user_id}`} className="hover:text-accent">
                        {p.user_id.slice(0, 14)}…
                      </Link>
                    ) : "—"}
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {p.product_type ?? "—"}
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {formatTs(p.created_at)}
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {formatTs(p.updated_at)}
                  </TD>
                  <TD className="font-mono text-[11px] text-ink-300">
                    {p.run_count ?? 0}
                  </TD>
                  <TD className="font-mono text-[11px]">
                    {p.user_id && p.id && p.latest_run_id ? (
                      <Link
                        to={`/users/${p.user_id}/products/${p.id}/runs/${p.latest_run_id}`}
                        className="text-ink-100 hover:text-accent"
                      >
                        {p.latest_run_id.slice(0, 16)}…
                      </Link>
                    ) : (
                      <span className="text-ink-500">—</span>
                    )}
                    {p.latest_run_status && (
                      <span className="ml-2 text-[10px] text-ink-500">
                        {p.latest_run_status}
                      </span>
                    )}
                  </TD>
                </TR>
              ))}
              {q.data.length === 0 && (
                <TR>
                  <TD>
                    <span className="text-ink-400">No products recorded yet.</span>
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        )}
      </main>
    </div>
  );
}
